/* eslint-disable prettier/prettier */
import { useRef, useState, useEffect, useCallback } from 'react';

import { Cinema, BookedSeat } from '@app/types';
import Loading from '@app/components/loading';

import styles from '../movie-tickets/movie-tickets.module.css';

type TicketSelectProps = {
  seats: BookedSeat[];
  toggleSeatSelect: (seatId: number) => void;
  loading: boolean;
  cinema?: Cinema;
};

export default function TicketSelect({
  seats,
  toggleSeatSelect,
  loading = false,
  cinema,
}: TicketSelectProps): JSX.Element {
  const width = 980;
  const [showMoreLeft, setShowMoreLeft] = useState(false);
  const [showMoreRight, setShowMoreRight] = useState(false);
  const container = useRef<HTMLDivElement | null>();
  const [isScrolled, setIsScrolled] = useState(false);
  const onScrollEvent = (left: number) => {
    setShowMoreLeft(true);
    setShowMoreRight(true);
    if (left === 0) {
      setShowMoreLeft(false);
    } else if (left === width - (container.current ? container.current.clientWidth : 0)) {
      setShowMoreRight(false);
    }
  };

  const scrollTo50Percent = useCallback(() => {
    if (container && container.current && !isScrolled) {
      container.current.scrollTo({
        left: width/2 - (container.current.clientWidth / 2),
        behavior: 'smooth',
      });

      setIsScrolled(true)
    }
  }, [isScrolled]);

  useEffect(() => {
    if (!loading && seats.length > 0) {
      scrollTo50Percent();
    }
    return undefined;
  }, [seats, loading, scrollTo50Percent]);

  return (
    <div className="relative">
      <div
        ref={(div) => {
          container.current = div;
        }}
        className="w-full overflow-x-scroll lg:overflow-hidden"
        onScroll={(e) => onScrollEvent(e.currentTarget.scrollLeft)}>
        <div
          style={{ width: `${width}px` }}
          className="flex flex-wrap  flex-row row gap-y-4 relative">
          {seats.map((seat) => (
            <div
              className="px-2"
              style={{ flex: `1 0 ${cinema?.seatsPerRow === 15 ? '6.5%' : '10%'}` }}
              key={seat.id}>
              <button
                type="button"
                disabled={loading}
                data-status={seat.status}
                onClick={() => {
                  toggleSeatSelect(seat.id);
                }}
                className={`${styles.seatBtn} ${
                  seat.status === 'available' ? styles.seatAvailable : null
                } ${seat.status === 'selected' ? styles.seatSelected : null} ${
                  seat.status === 'restricted' ? styles.seatRestricted : null
                } ${seat.status === 'temporary-blocked' ? styles.seatTemporaryBlocked : null} ${
                  seat.status === 'unavailable' ? styles.seatUnavailable : null
                }`}>
                {seat.label}
              </button>
            </div>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex w-full flex-col justify-center h-full fixed top-0 left-0 bg-opacity-80 bg-black">
          <Loading />
        </div>
      ) : null}
      {showMoreLeft ? <div className="show-left-shades lg:hidden" /> : null}
      {showMoreRight ? <div className="show-right-shades lg:hidden" /> : null}
    </div>
  );
}
